import React, { useState } from "react";
import {
    ShieldCheck,
    Users,
    Clock,
    BookOpen,
    Phone,
    Monitor,
    Wrench,
    Server,
    FileText,
    ArrowRight,
    Mail,
} from "lucide-react";
import DemoRequestModal from "@/components/DemoRequestModal";
import { TopBar, FooterBar, GradientOrbVisual } from "@/components/marketing/Chrome";

/**
 * Support — /support. Last of the pages from the original reference set.
 * "Contact Support" everywhere here opens the real DemoRequestModal (the
 * only working contact mechanism this site has) rather than a fake
 * ticket/chat destination -- same pattern as Documentation's and
 * UseCases' support links.
 */
export default function Support() {
    const [mobileOpen, setMobileOpen] = useState(false);
    const [contactOpen, setContactOpen] = useState(false);
    const openContact = () => setContactOpen(true);

    return (
        <div className="relative min-h-screen overflow-x-hidden bg-[#050e22] text-white" data-testid="support-page">
            <div className="pointer-events-none fixed inset-0 grid-bg opacity-[0.1]" />

            <TopBar mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} openContact={openContact} />
            <Hero openContact={openContact} />
            <SupportOptions openContact={openContact} />
            <ContactAndResources openContact={openContact} />
            <FooterBar openContact={openContact} />
            <DemoRequestModal open={contactOpen} onClose={() => setContactOpen(false)} />
        </div>
    );
}

function Hero({ openContact }) {
    const POINTS = [
        { icon: ShieldCheck, title: "Expert guidance", body: "From deployment to complex migrations." },
        { icon: Users, title: "Real support", body: "Talk to engineers, not bots." },
        { icon: Clock, title: "Keep moving", body: "Minimize downtime and stay on track." },
    ];
    return (
        <section className="relative z-10 overflow-hidden border-b border-white/5 px-6 pb-16 pt-14 sm:px-6 sm:pt-16" data-testid="support-hero">
            <div className="pointer-events-none absolute -right-40 top-0 h-[480px] w-[640px] rounded-full bg-[#0066ff]/25 blur-[140px]" />
            <div className="relative mx-auto grid max-w-[1600px] gap-10 lg:grid-cols-[1.15fr_1fr] lg:items-center">
                <div>
                    <div className="font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-[#3fb8ff]">Support</div>
                    <h1 className="mt-4 font-display text-4xl font-bold leading-[1.08] tracking-tight sm:text-5xl">
                        We're here to <span className="text-[#3fb8ff]">help.</span>
                    </h1>
                    <p className="mt-5 max-w-lg text-[15px] leading-relaxed text-white/65">
                        Get the support, resources, and answers you need to move your data with confidence.
                    </p>
                    <div className="mt-8 flex flex-wrap gap-x-8 gap-y-4">
                        {POINTS.map((p) => (
                            <div key={p.title} className="flex items-start gap-2.5">
                                <p.icon size={18} className="mt-0.5 shrink-0 text-[#3fb8ff]" />
                                <div>
                                    <div className="text-[13px] font-semibold text-white/85">{p.title}</div>
                                    <div className="text-[11.5px] text-white/50">{p.body}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                <GradientOrbVisual
                    heading={<>Move forward<br />with confidence.</>}
                    body={
                        <>EXPERT SUPPORT<br />REAL SOLUTIONS<br />SUCCESSFUL MIGRATIONS</>
                    }
                />
            </div>
        </section>
    );
}

const OPTIONS = [
    { icon: BookOpen, title: "Help Centre", body: "Browse articles, guides, and best practices for Windows data migration.", to: "/documentation" },
    { icon: Phone, title: "Contact Support", body: "Get in touch with our technical support team.", action: true },
    { icon: Monitor, title: "System Requirements", body: "Check supported Windows versions, environments, and hardware requirements.", to: "/how-it-works" },
    { icon: Wrench, title: "Troubleshooting", body: "Find solutions to common issues and error messages.", action: true },
    { icon: Server, title: "Deployment Help", body: "Get guidance on installation, configuration, and enterprise deployment.", to: "/capabilities" },
    { icon: FileText, title: "Product Documentation", body: "Access the full documentation library, including guides and references.", to: "/documentation" },
];

function SupportOptions({ openContact }) {
    return (
        <section className="relative z-10 px-6 py-20 sm:px-6" data-testid="support-options">
            <div className="mx-auto max-w-[1600px]">
                <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#3fb8ff]">Support Options</div>
                <h2 className="mt-2 font-display text-2xl font-bold tracking-tight sm:text-3xl">Find the support you need.</h2>
                <p className="mt-3 max-w-2xl text-[13.5px] leading-relaxed text-white/60">
                    Browse our support resources or get in touch with our team. We're here to help at every step of your migration.
                </p>
                <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {OPTIONS.map((o) => {
                        const Icon = o.icon;
                        const content = (
                            <>
                                <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-[#00C2FF]/30 bg-[#00C2FF]/10 text-[#3fb8ff]">
                                    <Icon size={17} />
                                </div>
                                <div className="mt-3 text-[13.5px] font-bold text-white">{o.title}</div>
                                <p className="mt-1 text-[12px] leading-relaxed text-white/55">{o.body}</p>
                            </>
                        );
                        const cls = "group flex flex-col items-start rounded-xl border border-white/10 bg-white/[0.03] p-5 text-left transition hover:border-[#00C2FF]/30 hover:bg-white/[0.045]";
                        return o.action ? (
                            <button key={o.title} type="button" onClick={openContact} className={cls}>
                                {content}
                                <ArrowRight size={14} className="mt-3 text-white/25 transition group-hover:translate-x-0.5 group-hover:text-[#3fb8ff]" />
                            </button>
                        ) : (
                            <a key={o.title} href={o.to} className={cls}>
                                {content}
                                <ArrowRight size={14} className="mt-3 text-white/25 transition group-hover:translate-x-0.5 group-hover:text-[#3fb8ff]" />
                            </a>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

const POPULAR_RESOURCES = [
    "Getting Started with MoovBytes",
    "System Requirements",
    "Installing MoovBytes",
    "Migration Best Practices",
    "Understanding Migration Reports",
];

const COMMON_ISSUES = [
    "Connection failures to source server",
    "Insufficient storage space",
    "Migration stuck or running slowly",
    "File permissions and access errors",
    "Unexpected migration results",
];

function ContactAndResources({ openContact }) {
    return (
        <section className="relative z-10 border-t border-white/5 bg-white/[0.015] px-6 py-20 sm:px-6" data-testid="support-contact">
            <div className="mx-auto grid max-w-[1600px] gap-6 lg:grid-cols-3">
                <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-7">
                    <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#3fb8ff]">Still need help?</div>
                    <h3 className="mt-2 font-display text-xl font-bold">Contact our support team.</h3>
                    <p className="mt-3 text-[13px] leading-relaxed text-white/60">
                        Can't find what you're looking for? Our team is ready to assist you with technical questions, deployment scenarios, or any other support needs.
                    </p>
                    <button
                        type="button"
                        onClick={openContact}
                        className="mt-5 inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-[#00C2FF] to-[#0066ff] px-5 py-3 text-[13px] font-semibold text-white shadow-lg shadow-[#004AAD]/30 transition hover:brightness-110"
                    >
                        <Mail size={15} />
                        Contact Support
                    </button>
                    <p className="mt-3 text-[11.5px] text-white/40">We typically respond within one business day.</p>
                </div>

                <ResourceList title="Popular resources." subtitle="Get quick answers to common questions and tasks." items={POPULAR_RESOURCES} icon={FileText} />
                <ResourceList title="Common issues." subtitle="Find solutions to frequently encountered issues." items={COMMON_ISSUES} icon={Wrench} />
            </div>
        </section>
    );
}

function ResourceList({ title, subtitle, items, icon: Icon }) {
    return (
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-7">
            <h3 className="font-display text-xl font-bold">{title}</h3>
            <p className="mt-2 text-[12.5px] leading-relaxed text-white/55">{subtitle}</p>
            <ul className="mt-5 space-y-2.5">
                {items.map((item) => (
                    <li key={item} className="flex items-center gap-2.5 rounded-lg border border-white/5 bg-white/[0.02] px-3 py-2.5 text-[12.5px] text-white/75">
                        <Icon size={13} className="shrink-0 text-[#3fb8ff]" />
                        <span className="truncate">{item}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
}
